import { useState } from "react";
import { CreditCard, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { Chip, Empty, Field, NSelect, Plate, useFetch } from "@/components/common";
import { ConfirmDialog } from "@/components/DemoCleanup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg, fDate, fEur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const PROVIDERS = [{ value: "mooncard", label: "Mooncard" }, { value: "fleetpro", label: "Fleet Pro" }];
const STATUS = { active: ["Active", "bg-emerald-50 text-emerald-700 border-emerald-200"], en_attente: ["En attente", "bg-amber-50 text-amber-700 border-amber-200"], bloquee: ["Bloquée", "bg-rose-50 text-rose-700 border-rose-200"], resiliee: ["Résiliée", "bg-slate-100 text-slate-600 border-slate-200"] };
const KINDS = [{ value: "commercial", label: "Commercial" }, { value: "logistique", label: "Logistique" }, { value: "administratif", label: "Titulaire administratif" }];

export const ProviderChip = ({ p }) => (
  <Chip testid="provider-chip" cls={p === "fleetpro" ? "bg-[#E8F1FB] text-[#1d5fa8] border-[#2F80ED]/30" : "bg-[#FEF3E2] text-[#9a5a05] border-[#F49619]/40"}>{p === "fleetpro" ? "Fleet Pro" : "Mooncard"}</Chip>
);

export const CardStatus = ({ v }) => <Chip testid="card-status-chip" cls={STATUS[v]?.[1]}>{STATUS[v]?.[0] || v}</Chip>;

export const useCanEdit = () => {
  const { user } = useAuth();
  return user.role === "admin" && user.real_role !== "direction";
};

function CardDialog({ card, onClose, onDone }) {
  const [f, setF] = useState({ holder_name: "", kind: "logistique", status: "active", card_number: "", fuel_types: "", activation_date: "", aliases: [], notes: "", ...card });
  const users = useFetch("/users");
  const drivers = useFetch("/drivers");
  const vehs = useFetch("/vehicles");
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const run = async () => {
    const body = { ...f, card_number: f.card_number || null, activation_date: f.activation_date || null, fuel_types: f.fuel_types || null };
    try {
      if (card?.id) await api.patch(`/fuel-cards/${card.id}`, body);
      else await api.post("/fuel-cards", body);
      toast.success("Carte enregistrée");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <ConfirmDialog title={card?.id ? "Modifier la carte Fleet Pro" : "Nouvelle carte Fleet Pro"} onConfirm={run} onClose={onClose} label="Enregistrer" testid="fuel-card-dialog" primary wide>
      <p className="text-xs text-rose-700">Ne saisissez jamais le code PIN de la carte : il n'est ni demandé ni enregistré.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Titulaire"><Input value={f.holder_name} onChange={(e) => set("holder_name")(e.target.value)} data-testid="card-holder-input" /></Field>
        <Field label="Usage"><NSelect value={f.kind} onChange={set("kind")} options={KINDS} testid="card-kind-select" /></Field>
        <Field label="Numéro / identifiant de carte"><Input value={f.card_number || ""} onChange={(e) => set("card_number")(e.target.value)} data-testid="card-number-input" /></Field>
        <Field label="Statut"><NSelect value={f.status} onChange={set("status")} options={Object.entries(STATUS).map(([value, [label]]) => ({ value, label }))} testid="card-status-select" /></Field>
        <Field label="Carburant autorisé"><Input value={f.fuel_types || ""} onChange={(e) => set("fuel_types")(e.target.value)} placeholder="ex. Gazole, SP95-E10" data-testid="card-fuel-input" /></Field>
        <Field label="Date d'activation"><Input type="date" value={f.activation_date || ""} onChange={(e) => set("activation_date")(e.target.value)} data-testid="card-activation-input" /></Field>
        <Field label="Profil rattaché"><NSelect value={f.user_id || ""} onChange={set("user_id")} placeholder="Aucun" testid="card-user-select" options={(users.data || []).filter((u) => !u.is_demo).map((u) => ({ value: u.id, label: u.name }))} /></Field>
        {f.kind === "logistique" && <Field label="Chauffeur (référentiel)"><NSelect value={f.driver_id || ""} onChange={set("driver_id")} placeholder="Aucun" testid="card-driver-select" options={(drivers.data || []).filter((d) => !d.is_demo).map((d) => ({ value: d.id, label: d.name }))} /></Field>}
        {f.kind === "logistique" && <Field label="Véhicule habituel"><NSelect value={f.vehicle_id || ""} onChange={set("vehicle_id")} placeholder="Aucun" testid="card-vehicle-select" options={(vehs.data || []).filter((v) => !v.is_demo && v.type === "livraison").map((v) => ({ value: v.id, label: v.plate }))} /></Field>}
        <Field label="Autres libellés reconnus à l'import (séparés par des virgules)"><Input value={(f.aliases || []).join(", ")} onChange={(e) => set("aliases")(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="card-aliases-input" /></Field>
      </div>
    </ConfirmDialog>
  );
}

export function CardsTable({ kind, extra, testid = "fuel-cards-table" }) {
  const edit = useCanEdit();
  const { data, reload } = useFetch("/fuel-cards", kind ? { kind } : {});
  const [open, setOpen] = useState(null);
  const rows = data || [];
  return (
    <div data-testid={testid}>
      {edit && <div className="flex justify-end p-3 border-b border-slate-100"><Button size="sm" onClick={() => setOpen({ kind: kind || "commercial" })} className="bg-[#0E2D43] hover:bg-[#163f5e]" data-testid="add-fuel-card"><Plus className="w-4 h-4 mr-1" /> Ajouter une carte</Button></div>}
      {!rows.length ? <Empty text="Aucune carte Fleet Pro" /> : (
        <div className="overflow-x-auto">
          <table className="tbl w-full">
            <thead><tr><th>Titulaire</th><th>Usage</th><th>Carte</th><th>Statut</th><th>Carburant</th><th>Activation</th><th>Véhicule</th>{extra && <th className="text-right">Dépenses</th>}{edit && <th></th>}</tr></thead>
            <tbody>{rows.map((c) => {
              const x = extra?.[c.id];
              return (
                <tr key={c.id} data-testid={`fuel-card-${c.id}`}>
                  <td className="font-semibold text-[#0E2D43]"><span className="inline-flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#2F80ED]" />{c.holder_name}</span></td>
                  <td className="text-xs">{c.kind_label}</td>
                  <td className="font-mono text-xs">{c.card_masked || <span className="text-slate-400">à compléter</span>}</td>
                  <td><CardStatus v={c.status} /></td>
                  <td className="text-xs">{c.fuel_types || "—"}</td>
                  <td className="text-xs">{c.activation_date ? fDate(c.activation_date) : "—"}</td>
                  <td>{c.plate ? <Plate>{c.plate}</Plate> : "—"}</td>
                  {extra && <td className="text-right tabular-nums font-semibold">{x ? `${fEur(x.spend)} · ${x.tx_count} tx` : "—"}</td>}
                  {edit && <td><Button size="sm" variant="outline" onClick={() => setOpen(c)} data-testid={`edit-fuel-card-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button></td>}
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
      {open && <CardDialog card={open} onClose={() => setOpen(null)} onDone={() => { setOpen(null); reload(); }} />}
    </div>
  );
}

export function CardAssignDialog({ t, onClose, onDone }) {
  const cards = useFetch("/fuel-cards");
  const vehs = useFetch("/vehicles");
  const [c, setC] = useState(t.card_id || "");
  const [v, setV] = useState(t.suggested_vehicle_id || "");
  const card = (cards.data || []).find((x) => x.id === c);
  const run = async () => {
    try {
      await api.patch(`/fuel/transactions/${t.id}/assign-card`, { card_id: c, vehicle_id: v || null });
      toast.success("Transaction rattachée");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <ConfirmDialog title="Rattacher la transaction Fleet Pro" onConfirm={run} onClose={onClose} label="Rattacher" testid="fuel-card-assign-dialog" primary>
      <p>{fDate(t.date)} · {fEur(t.amount_ttc)} · fichier : « {t.commercial_name || "—"} » / « {t.plate || "—"} »</p>
      {t.review_reason && <p className="text-xs text-amber-700">{t.review_reason}</p>}
      <Field label="Carte / titulaire"><NSelect value={c} onChange={setC} placeholder="Choisir…" testid="fuel-assign-card" options={(cards.data || []).map((x) => ({ value: x.id, label: `${x.holder_name} — ${x.kind_label}` }))} /></Field>
      <Field label={card?.kind === "commercial" ? "Véhicule commercial (obligatoire)" : "Véhicule (facultatif)"}><NSelect value={v} onChange={setV} placeholder="Choisir…" testid="fuel-assign-card-vehicle" options={(vehs.data || []).filter((x) => !x.is_demo && (!card || card.kind !== "commercial" || x.type === "commercial")).map((x) => ({ value: x.id, label: x.plate }))} /></Field>
    </ConfirmDialog>
  );
}
